import { format } from 'date-fns-tz';
import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import type { DeliverySlot } from '~/api/ETA';
import { company } from '~/company/Company';
import { useDateLang } from '~/hooks/useDateLang';
import { useTimeFormat } from '~/hooks/useTimeFormat';
import { cartStore, checkoutStore, mainStore, orderStore } from '~/stores';
import { formatPriceWithCurrency } from '~/utils/formaters';

import {
  AvailableOffersForSlot,
  compareLeadTime,
} from '../AvailableOffersForSlot';

import styles from './SlotItem.module.scss';

interface SlotItemProps {
  slot: DeliverySlot;
  isSelected: boolean;
  onSelect: (slotId: string, hasAvailableProducts?: boolean) => void;
  priceWithoutDelivery: number;
}

const SlotItem: FC<SlotItemProps> = ({
  slot,
  isSelected,
  onSelect,
  priceWithoutDelivery,
}) => {
  const { t } = useTranslation();
  const { locale } = useDateLang();
  const { timeFormat } = useTimeFormat();

  const {
    schedule_slot_id,
    delivery_min_time,
    delivery_max_time,
    delivery_price,
    order_amount_threshold,
    free_delivery_threshold,
  } = slot;

  const currentDelivery = checkoutStore.slotSelectionModalStore.selectedSlot;

  const currentItems = cartStore.selectedCartItems.filter(({ sku }) =>
    currentDelivery?.itemsIds.includes(sku),
  );

  const unavailableProducts = checkoutStore.isDeliverTogether
    ? currentItems.filter((cartItem) => {
        const { count, sellableWarehouses, backorderLeadTime } = cartItem;
        const currentWHStock = Number(
          sellableWarehouses[`${orderStore.etaCalculation?.warehouse.code}`] ||
            '0',
        );

        if (currentWHStock >= count && orderStore.isExpressAvailableNow) {
          return false;
        }
        return compareLeadTime(delivery_min_time, backorderLeadTime);
      })
    : [];

  const getSlotPrice = () => {
    const deliveryPrice = +mainStore.convertPenceToPounds(delivery_price);
    const deliveryTreshold = +mainStore.convertPenceToPounds(
      free_delivery_threshold,
    );
    const orderAmountThreshold = +mainStore.convertPenceToPounds(
      order_amount_threshold ?? 0,
    );

    const isFreeDelivery =
      deliveryPrice === 0 || deliveryTreshold <= priceWithoutDelivery;

    return {
      minOrderAmount: orderAmountThreshold > 0 ? orderAmountThreshold : null,
      deliveryFee: isFreeDelivery ? null : deliveryPrice,
      isFreeDelivery,
    };
  };

  const { minOrderAmount, deliveryFee, isFreeDelivery } = getSlotPrice();

  const handleSelect = () => {
    onSelect(schedule_slot_id, unavailableProducts.length > 0);
  };

  return (
    <label
      htmlFor={schedule_slot_id}
      className={classes([styles.slotItem, isSelected && styles.checked])}
    >
      <div className={styles.slotLabel}>
        <input
          type="radio"
          name="time"
          id={schedule_slot_id}
          checked={isSelected}
          onChange={handleSelect}
        />
        <div className={styles.slotItemContent}>
          <div className={styles.timeBlock}>
            <div className={styles.timeRange}>
              {format(new Date(delivery_min_time * 1000), timeFormat, {
                timeZone: company.config.timeZone,
                locale,
              })}
              {' - '}
              {format(new Date(delivery_max_time * 1000), timeFormat, {
                timeZone: company.config.timeZone,
                locale,
              })}
            </div>
            {minOrderAmount && (
              <div className={styles.minOrderAmount}>
                {t('checkout:minimumOrderValue', {
                  price: formatPriceWithCurrency(minOrderAmount),
                })}
              </div>
            )}
          </div>
        </div>
        <span
          className={classes([
            styles.deliveryFee,
            isFreeDelivery && styles.freeDelivery,
          ])}
        >
          {isFreeDelivery
            ? t('checkout:deliveryFree')
            : formatPriceWithCurrency(deliveryFee!)}
        </span>
      </div>

      <AvailableOffersForSlot
        currentItems={currentItems}
        unavailableProducts={unavailableProducts}
      />
    </label>
  );
};

export default observer(SlotItem);
